import { Container, Typography, Paper, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/theme';

export default function PrivacyPage() {
  const { t } = useTranslation();
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#FFFFFF' }}>{t('pages.privacy.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Last updated: March 2026</Typography>

        <Section title="1. Information We Collect">
          <strong>Account Information:</strong> Phone number, email address, first name, last name, father's name (optional), and profile photo.{'\n\n'}
          <strong>Event Activity:</strong> Events you attend, vouchers purchased, ratings submitted, and group participation.{'\n\n'}
          <strong>Payment Information:</strong> We do NOT store credit card numbers or bank details. All payment processing is handled by authorized third-party gateways (GoldenPay, PashaPay). We store only transaction reference IDs for reconciliation.{'\n\n'}
          <strong>Device Information:</strong> Device type, operating system, and browser type for security and service improvement purposes.
        </Section>

        <Section title="2. How We Use Your Information">
          To provide and maintain the CoffeeMates service. To match you with events and group participants. To process voucher purchases and refunds. To send transactional notifications (event confirmations, QR codes, refund receipts). To enforce community safety standards. To improve our service through aggregated, anonymized analytics.
        </Section>

        <Section title="3. Information Sharing">
          <strong>With Event Participants:</strong> Your first name and avatar are visible to other group members after matching.{'\n\n'}
          <strong>With Partner Venues:</strong> Your first name and last initial are shared with the partner cafe hosting your event, along with your voucher tier.{'\n\n'}
          <strong>With Payment Processors:</strong> Your payment information is shared directly with our payment gateway partners to process transactions.{'\n\n'}
          <strong>We Never:</strong> Sell your personal data to third parties. Share your full name, phone number, or email with other users. Use your data for third-party advertising.
        </Section>

        <Section title="4. Data Security">
          All data is transmitted over encrypted connections (HTTPS/TLS). Passwords are hashed using bcrypt (industry standard). Database is hosted on AWS RDS with encryption at rest. Access to user data is restricted to authorized personnel only.
        </Section>

        <Section title="5. Safety & Reporting Data">
          Abuse reports are stored securely and reviewed by the CoffeeMates safety team. Reporter anonymity is preserved when requested. Banned users' identity information (phone, email, full name) is retained in a blacklist to prevent re-registration.
        </Section>

        <Section title="6. Data Retention">
          Account data is retained for as long as your account is active. Event history is retained for 2 years for quality and dispute resolution purposes. You may request deletion of your account and associated data at any time. Blacklist entries for banned users are retained permanently for safety.
        </Section>

        <Section title="7. Your Rights">
          <strong>Access:</strong> You can view all personal data we hold about you in your Profile.{'\n\n'}
          <strong>Correction:</strong> You can update your personal information at any time.{'\n\n'}
          <strong>Deletion:</strong> You can request account deletion by contacting support@coffeemates.biz.{'\n\n'}
          <strong>Data Export:</strong> You can request a copy of your data in a standard format.
        </Section>

        <Section title="8. Cookies & Local Storage">
          CoffeeMates uses browser local storage to maintain your login session (JWT token). We do not use tracking cookies or third-party analytics that track individual users.
        </Section>

        <Section title="9. Children's Privacy">
          CoffeeMates is not intended for users under 18 years of age. We do not knowingly collect information from children.
        </Section>

        <Section title="10. Changes to This Policy">
          We may update this Privacy Policy from time to time. We will notify users of material changes via email. The "Last updated" date at the top of this page indicates when the policy was last revised.
        </Section>

        <Section title="11. Contact Us">
          For privacy-related questions or data requests, contact: support@coffeemates.biz
        </Section>
      </Paper>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Typography variant="body1" component="div" sx={{ color: colors.neutral[700], lineHeight: 1.7, whiteSpace: 'pre-line' }}>{children}</Typography>
    </Box>
  );
}
